import { IMPACT_CONFIRM_BPS } from 'utils/swap/types'

export type ButtonState =
  | 'connect'
  | 'wrongChain'
  | 'enterAmount'
  | 'insufficientBalance'
  | 'quoting'
  | 'noRoute'
  | 'quoteUnavailable'
  | 'approve'
  | 'approving'
  | 'confirmImpact'
  | 'swap'
  | 'swapping'

export interface ButtonInput {
  connected: boolean
  onCorrectChain: boolean
  amountIn: bigint
  balance: bigint | null
  quoting: boolean
  hasQuote: boolean
  quoteError: 'none' | 'noRoute' | 'unavailable'
  needsApproval: boolean
  step: 'idle' | 'approving' | 'swapping'
  priceImpactBps?: number
  impactAcknowledged: boolean
}

/**
 * Which single thing the button is for right now.
 *
 * A button that offers a swap while the balance is short, or an approval while
 * the wallet is on the wrong chain, is a button that produces a failure the
 * user could have been spared. The order below is the order the obstacles
 * actually stand in, and it is a pure function so it can be argued with in a
 * test rather than in a wallet.
 */
export function swapButtonState(input: ButtonInput): ButtonState {
  // A transaction already in flight owns the button, whatever else changed.
  if (input.step === 'approving') return 'approving'
  if (input.step === 'swapping') return 'swapping'

  if (!input.connected) return 'connect'
  if (!input.onCorrectChain) return 'wrongChain'
  if (input.amountIn <= 0n) return 'enterAmount'

  /*
   * Before the routes, not after. A wallet that cannot cover the trade will
   * fail at the transfer whichever route is chosen, so saying so first spares
   * a quote round and an approval — and it beats `approve` deliberately: an
   * approval for money the user does not have is a fee paid for nothing.
   */
  if (input.balance !== null && input.balance < input.amountIn) return 'insufficientBalance'

  if (input.quoting && !input.hasQuote) return 'quoting'
  if (input.quoteError === 'unavailable') return 'quoteUnavailable'
  if (!input.hasQuote) return 'noRoute'

  if (input.needsApproval) return 'approve'

  /*
   * Above fifteen per cent the trade is losing a sixth of its value to the
   * pool, which is a decision rather than a setting. The button stays shut
   * until the user says they meant it.
   *
   * An impact that could not be computed is **not** an impact of zero, and
   * reading it as one is how a real trade got through: on 2026-09-09 a wallet
   * was allowed to sell 0.0075 ETH into a WETH/MPRO pool holding 0.058 WETH —
   * an eighth of the whole reserve. There was no reference quote to measure
   * against because the aggregator was rate-limited at that moment, so
   * `priceImpactBps` was `undefined`, `?? 0` made it harmless, and the button
   * opened. Unknown is the state that most deserves the checkbox, because it
   * arises exactly when the only route left is a thin one.
   */
  if (
    (input.priceImpactBps === undefined || input.priceImpactBps >= IMPACT_CONFIRM_BPS) &&
    !input.impactAcknowledged
  ) {
    return 'confirmImpact'
  }

  return 'swap'
}

/** Whether the button in this state does anything when pressed. */
export function isActionable(state: ButtonState): boolean {
  return state === 'connect' || state === 'wrongChain' || state === 'approve' || state === 'swap'
}
